import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { createDeliveryDriver } from '@vn-propane/shared/client';
import type { CreateDeliveryDriverRequest } from '@vn-propane/shared/client';
import { ArrowLeft } from 'lucide-react';

export default function NewDeliveryDriver() {
  const { id: orgId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['admin', 'common']);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState<CreateDeliveryDriverRequest>({
    full_name: '',
    gender: null,
    birthdate: null,
    gas_station_ids: [],
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId || !formData.full_name) return;

    setSaving(true);
    const result = await createDeliveryDriver({
      path: { org_id: orgId },
      body: formData,
    });

    if (result.error) {
      console.error('Failed to create delivery driver:', result.error);
      setSaving(false);
      return;
    }

    if (result.data) {
      navigate(`/organizations/${orgId}/drivers/${result.data.data.id}`);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <Link
          to={`/organizations/${orgId}/drivers`}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('deliveryDrivers.backToDrivers')}
        </Link>
      </div>

      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">{t('deliveryDrivers.addDeliveryDriver')}</h1>
          <p className="text-muted-foreground">
            {t('deliveryDrivers.addNewDriver')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">{t('deliveryDrivers.fullNameRequired')}</Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="gender">{t('deliveryDrivers.gender')}</Label>
              <Select
                value={formData.gender || ''}
                onValueChange={(value) => setFormData({ ...formData, gender: value || null })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('deliveryDrivers.selectGender')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">{t('deliveryDrivers.male')}</SelectItem>
                  <SelectItem value="female">{t('deliveryDrivers.female')}</SelectItem>
                  <SelectItem value="other">{t('deliveryDrivers.other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="birthdate">{t('deliveryDrivers.birthdate')}</Label>
              <Input
                id="birthdate"
                type="date"
                value={formData.birthdate || ''}
                onChange={(e) => setFormData({ ...formData, birthdate: e.target.value || null })}
              />
            </div>
          </div>

          <div className="flex gap-4">
            <Button type="submit" disabled={!formData.full_name || saving}>
              {saving ? t('deliveryDrivers.creating') : t('deliveryDrivers.createDeliveryDriver')}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/organizations/${orgId}/drivers`)}
            >
              {t('common:buttons.cancel')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
